import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { getSettings } from "../../services/settingsServices";
import { toAbsolutePhotoUrl } from "../../services/settingsPhotoServices";
import SectionTitle from "../../components/ui/SectionTitle";

function cn(...c) {
  return c.filter(Boolean).join(" ");
}

const AboutSkeleton = () => (
  <section className="relative py-14 sm:py-18 bg-slate-50 dark:bg-slate-950">
    <div className="mx-auto max-w-7xl px-4">
      <div className="grid gap-8 lg:grid-cols-12 items-center">
        <div className="lg:col-span-4">
          <div className="aspect-square rounded-3xl bg-slate-200/70 dark:bg-slate-800 animate-pulse" />
        </div>
        <div className="lg:col-span-8">
          <div className="h-8 w-2/3 rounded-xl bg-slate-200/70 dark:bg-slate-800 animate-pulse" />
          <div className="mt-4 h-4 w-1/2 rounded bg-slate-200/70 dark:bg-slate-800 animate-pulse" />
          <div className="mt-8 space-y-3">
            <div className="h-4 w-full rounded bg-slate-200/70 dark:bg-slate-800 animate-pulse" />
            <div className="h-4 w-[92%] rounded bg-slate-200/70 dark:bg-slate-800 animate-pulse" />
            <div className="h-4 w-[85%] rounded bg-slate-200/70 dark:bg-slate-800 animate-pulse" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

const About = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await getSettings();
        if (Array.isArray(data) && data.length > 0) setSettings(data[0]);
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const safeHtml = useMemo(() => {
    const raw = settings?.about_content || "";
    return DOMPurify.sanitize(raw, {
      USE_PROFILES: { html: true },
      FORBID_ATTR: ["style"], // ✅ retire les couleurs inline
    });
  }, [settings?.about_content]);

  if (loading) return <AboutSkeleton />;
  if (!settings) return null;

  const FALLBACK_LOGO = "/img/no-image.jpg";
  const logoSrc =
    toAbsolutePhotoUrl(settings?.logo_club?.trim() || "") || FALLBACK_LOGO;

  return (
    <section
      id="about"
      className={cn(
        "relative py-14 sm:py-18",
        "bg-lightBackground dark:bg-darkBackground",
        "text-slate-900 dark:text-slate-50",
      )}
    >
      {/* Décor vitrine discret */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0",
          "bg-[radial-gradient(900px_circle_at_10%_20%,rgba(99,102,241,0.10),transparent_55%),radial-gradient(700px_circle_at_85%_30%,rgba(236,72,153,0.08),transparent_55%)]",
        )}
      />

      {/* CSS local (animations soft) */}
      <style>{`
        @keyframes ab-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        @media (prefers-reduced-motion: reduce) {
          .ab-anim { animation: none !important; transition: none !important; }
        }
      `}</style>

      <div className="relative mx-auto max-w-7xl px-4">
        <div className="grid gap-8 lg:grid-cols-12 items-center">
          {/* Logo card */}
          <div className="lg:col-span-4">
            <div className={cn("relative overflow-hidden rounded-3xl")}>
              {/* anneau primary discret */}
              <div
                aria-hidden
                className="absolute inset-0 rounded-3xl ring-1 ring-primary/20"
              />

              {/* petit glow */}
              <div
                aria-hidden
                className="ab-anim absolute -top-10 -left-10 h-40 w-40 rounded-full bg-primary/18 blur-3xl"
                style={{ animation: "ab-float 3.2s ease-in-out infinite" }}
              />

              <div className="p-6 sm:p-8">
                <div className="aspect-square w-full rounded-2xl flex items-center justify-center overflow-hidden">
                  <img
                    src={logoSrc}
                    alt="Logo du club"
                    loading="lazy"
                    className={cn(
                      "max-h-full max-w-full object-contain",
                      "drop-shadow-md",
                      "transition duration-300",
                      "hover:scale-[1.02]",
                    )}
                    onError={(e) => {
                      e.currentTarget.src = FALLBACK_LOGO;
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Texte card */}
          <div className="lg:col-span-8">
            <div
              className={cn(
                "rounded-3xl overflow-hidden",
                "border border-slate-200/70 dark:border-slate-800",
                "bg-white/75 dark:bg-slate-900/60 backdrop-blur",
                "shadow-sm",
              )}
            >
              <div className="p-6 sm:p-10">
                <SectionTitle
                  align="left"
                  title={settings.about_title || "À propos"}
                  subtitle={settings.about_subtitle || ""}
                />

                <div
                  className={cn(
                    "mt-8",
                    "prose prose-slate dark:prose-invert max-w-none",
                    "prose-p:leading-relaxed prose-p:text-[15px] sm:prose-p:text-[16px]",

                    // ✅ FORCE le gras en blanc (spécificité + !important)
                    "dark:[&_strong]:!text-white dark:[&_b]:!text-white",

                    // (optionnel) si tu veux que le gras soit encore plus visible
                    "dark:[&_strong]:font-extrabold dark:[&_b]:font-extrabold",
                  )}
                  dangerouslySetInnerHTML={{ __html: safeHtml }}
                />
              </div>

              {/* bandeau bas léger */}
              <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-200/80 dark:via-slate-800 to-transparent" />
              <div className="px-6 sm:px-10 py-4 text-xs text-slate-500 dark:text-slate-400">
                {settings.club_name ? settings.club_name : " "}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
